import { AnalyticsSummaryResponse, PlatformOverviewResponse } from '../types/analytics';
import { auth } from '../config/firebase';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.socialness.ai';
const MANAGEMENT_API_URL = `${API_URL}/management`;

export interface AnalyticsSummaryParams {
  start: string;
  end: string;
  brandId?: string;
  applanguage?: string;
  enabled?: boolean;
  refresh?: string | null;
  withAnalysis?: boolean;
}

export interface PlatformOverviewParams {
  platform: string;
  start: string;
  end: string;
  brandId?: string;
  pageId?: string;
  applanguage?: string;
  withAnalysis?: boolean;
}

export interface OnDemandAiAnalysisParams {
  appLanguage: string;
  platform?: string; // Platform is optional, might not be needed if data includes it, or for summary
  data: string; // Stringified data from analyticsSummary or platformData
}

// Define the structure for individual key findings
export interface KeyFinding {
  metric: string;
  trend: string;
  significance: string;
  recommendation: string;
}

// Define the structure for the 'analysis' object in the response
export interface AiAnalysisData {
  summary: string;
  key_findings?: KeyFinding[];
  strategic_insights?: string[];
  content_recommendations?: string[];
  strenghts?: string[]; // Note: API returns 'strenghts' with a typo
  weaknesses?: string[];
  growth_opportunities?: string[];
}

export interface OnDemandAiAnalysisResponse {
  analysis: AiAnalysisData;
  platform?: string;
  timestamp?: string;
}

/**
 * Fetches analytics summary data from the API
 * @param params - Parameters for the analytics summary request
 * @returns Promise with the analytics summary data
 */
export const fetchAnalyticsSummary = async (params: AnalyticsSummaryParams): Promise<AnalyticsSummaryResponse> => {
  try {
    // Get the current user's token
    const token = await auth.currentUser?.getIdToken();
    
    if (!token) {
      throw new Error('User not authenticated');
    }

    // Build the query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('start', params.start);
    queryParams.append('end', params.end);

    // The "all" sentinel is a frontend-only value used when no specific brand
    // is selected. The backend treats it as a real brand ID and returns 404
    // ("Brand not found with ID: all"), so strip it before sending.
    if (params.brandId && params.brandId !== 'all') {
      queryParams.append('brandId', params.brandId);
    }

    if (params.applanguage) {
      queryParams.append('applanguage', params.applanguage);
    }
    if (params.withAnalysis !== undefined) {
      queryParams.append('withAnalysis', String(params.withAnalysis));
    }

    // Make the API request. /insights/summary now lives on the management
    // worker (not the retired insights.socialness.ai); using MANAGEMENT_API_URL
    // keeps it consistent with fetchPlatformOverview below.
    const response = await fetch(`${MANAGEMENT_API_URL}/insights/summary?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `API request failed with status ${response.status}`);
    }

    const data = await response.json();
    return data as AnalyticsSummaryResponse;
  } catch (error) {
    console.error('Error fetching analytics summary:', error);
    throw error;
  }
};

/**
 * Fetches platform-specific analytics data from the API
 * @param params - Parameters for the platform overview request
 * @returns Promise with the platform-specific analytics data
 */
export const fetchPlatformOverview = async (params: PlatformOverviewParams): Promise<PlatformOverviewResponse> => {
  try {
    // Get the current user's token
    const token = await auth.currentUser?.getIdToken();
    
    if (!token) {
      throw new Error('User not authenticated');
    }

    // Build the query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('platform', params.platform);
    queryParams.append('start', params.start);
    queryParams.append('end', params.end);

    if (params.brandId && params.brandId !== 'all') {
      queryParams.append('brandId', params.brandId);
    }

    if (params.pageId) {
      queryParams.append('pageId', params.pageId);
    }
    
    if (params.applanguage) {
      queryParams.append('applanguage', params.applanguage);
    }
    if (params.withAnalysis !== undefined) {
      queryParams.append('withAnalysis', String(params.withAnalysis));
    }
    
    
    const apiUrl = `${MANAGEMENT_API_URL}/insights/overview?${queryParams.toString()}`;
    console.log('Calling API:', apiUrl);

    // Make the API request
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `API request failed with status ${response.status}`);
    }

    const data = await response.json();
    console.log('Platform Overview API Response:', {
      platform: data.platform,
      pageId: data.pageId,
      currentBrand: data.currentBrand?.name,
      metrics: data.metrics?.length
    });
    return data as PlatformOverviewResponse;
  } catch (error) {
    console.error(`Error fetching ${params.platform} analytics:`, error);
    throw error;
  }
};

/**
 * Fetches on-demand AI analysis for given metrics data
 */
export const fetchOnDemandAiAnalysis = async (params: OnDemandAiAnalysisParams): Promise<OnDemandAiAnalysisResponse> => {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new Error('User not authenticated');
  }

  const body = {
    appLanguage: params.appLanguage,
    data: params.data,
    ...(params.platform && { platform: params.platform }), // Conditionally add platform
  };

  const response = await fetch(`${API_URL}/ai-coach/insights/analyze`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.error('On-demand AI Analysis API Error:', errorData);
    throw new Error(errorData.error || errorData.message || `On-demand AI analysis API request failed with status ${response.status}`);
  }
  return response.json();
}; 